import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { AccountGate } from '../account-gate.data'

/**
 * The last session-ending answer the API gave (decisions 15 / 16). The HTTP interceptor reports;
 * `FairAccountSessionHost` (AppLayout) signs out locally and shows the message / restore screen,
 * so the interceptor never imports the router or the UI. Repeated reports of the same gate (a
 * page fires several calls at once) collapse into one until it is handled.
 */
export const useAccountGateStore = defineStore('accountGate', () => {
  const pending = ref<AccountGate | null>(null)
  /** The restore screen's date when the 403 came from a call (not from the e-mail link). */
  const restoreAvailableUntil = ref<string | null>(null)
  /** Restore token from the e-mail link (`/account/restore?token=`). */
  const restoreToken = ref<string | null>(null)
  /** Why the sign-in screen opened by itself (shown above its fields until the next sign-in). */
  const sessionNotice = ref<'token_revoked' | 'user_not_provisioned' | null>(null)

  function setSessionNotice(notice: 'token_revoked' | 'user_not_provisioned' | null) {
    sessionNotice.value = notice
  }

  function report(gate: AccountGate) {
    if (pending.value?.kind === gate.kind) return
    pending.value = gate
    if (gate.kind === 'scheduled_for_deletion') restoreAvailableUntil.value = gate.restoreAvailableUntil
  }

  function take(): AccountGate | null {
    const gate = pending.value
    pending.value = null
    return gate
  }

  function setRestoreToken(token: string | null) {
    restoreToken.value = token && token.trim() ? token.trim() : null
  }

  return { pending, restoreAvailableUntil, restoreToken, sessionNotice, report, take, setRestoreToken, setSessionNotice }
})
